import {
  FaceLandmarker,
  FilesetResolver,
  type Classifications,
  type NormalizedLandmark,
} from '@mediapipe/tasks-vision';
import {
  aggregate,
  scoreFrame,
  type FrameScore,
  type Landmark,
} from './scorer/score.ts';
import {
  LIGHTING_REALTIME_WARN,
  SAMPLE_COUNT,
  SAMPLE_DEADLINE_SLACK_MS,
  SAMPLE_INTERVAL_MS,
} from './scorer/constants.ts';

export type ScoreFn<TScore> = (
  landmarks: NormalizedLandmark[],
  blendshapes: Classifications | undefined,
) => TScore | null;

interface ScorerEvents<TScore> {
  onSample?(index: number, t: number, score: TScore | null): void;
  onLighting?(warn: boolean): void;
}

export interface ScorerHandle<TScore = FrameScore> {
  ready: Promise<void>;
  start(t0: number): void;
  ingest(bitmap: ImageBitmap, t: number): void;
  stop(): Promise<{
    mean: number;
    visibility: number;
    samples: (TScore | null)[];
  }>;
  destroy(): void;
}

const FULL_MODEL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
// MediaPipe face_landmarker only ships a float16 quantization — there's no int8 variant.
const LITE_MODEL = FULL_MODEL;

const WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.17/wasm';

/**
 * Main-thread FaceLandmarker scorer.
 *
 * @mediapipe/tasks-vision@0.10 doesn't initialise cleanly inside ES module
 * Web Workers — its loader expects DOM globals that aren't present and
 * `createFromOptions` races the wasm registration, surfacing as
 * "ModuleFactory not set." We run on the main thread instead. Cost is
 * ~10–30ms per inference at 2fps; the WebRTC pipeline is already main-thread
 * and there's plenty of headroom.
 */
export interface StartScorerOpts<TScore = FrameScore> {
  network: 'fast' | 'slow' | 'unknown';
  /** Enable ARKit-compatible expression coefficients. Required for smile scoring. */
  withBlendshapes?: boolean;
  /** Per-frame scoring function. Defaults to the face/aesthetic scorer. */
  scoreFn?: ScoreFn<TScore>;
  /**
   * Run detection + scoreFn on every video frame, not just on the 20 sample
   * deadlines. Required for stateful counting modes (blink/nod/shake) where
   * transitions happen faster than the 500ms inter-sample interval — without
   * this, the closure never sees the open→closed→open edges. Storage of
   * `samples` still happens only on deadlines.
   */
  denseDetection?: boolean;
}

const defaultScoreFn: ScoreFn<FrameScore> = (lm) => scoreFrame(lm as Landmark[]);

function looksLikeFrameScores(samples: unknown[]): samples is (FrameScore | null)[] {
  return samples.some(
    (s) => s !== null && typeof (s as { raw?: unknown }).raw === 'number'
      && typeof (s as { sym?: unknown }).sym === 'number',
  );
}

export function startScorer<TScore = FrameScore>(
  opts: StartScorerOpts<TScore>,
  events: ScorerEvents<TScore> = {},
): ScorerHandle<TScore> {
  let landmarker: FaceLandmarker | null = null;
  let samples: (TScore | null)[] = [];
  let deadlines: number[] = [];
  let collecting = false;
  let destroyed = false;
  const scoreFn = (opts.scoreFn ?? (defaultScoreFn as unknown as ScoreFn<TScore>));
  const withBlendshapes = opts.withBlendshapes ?? false;
  const denseDetection = opts.denseDetection ?? false;

  const ready: Promise<void> = (async () => {
    const modelUrl = opts.network === 'slow' ? LITE_MODEL : FULL_MODEL;
    const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
    const lm = await FaceLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: modelUrl },
      runningMode: 'VIDEO',
      numFaces: 1,
      outputFaceBlendshapes: withBlendshapes,
      outputFacialTransformationMatrixes: false,
    });
    if (destroyed) {
      lm.close();
      return;
    }
    landmarker = lm;
  })();

  function nextDueIndex(now: number): number {
    for (let i = 0; i < deadlines.length; i++) {
      if (samples[i] !== null) continue;
      const d = deadlines[i]!;
      if (now >= d - SAMPLE_DEADLINE_SLACK_MS && now <= d + SAMPLE_DEADLINE_SLACK_MS * 2) {
        return i;
      }
    }
    return -1;
  }

  return {
    ready,
    start(t0) {
      samples = new Array(SAMPLE_COUNT).fill(null);
      deadlines = [];
      for (let i = 0; i < SAMPLE_COUNT; i++) {
        deadlines.push(t0 + 250 + i * SAMPLE_INTERVAL_MS);
      }
      collecting = true;
    },
    ingest(bitmap, t) {
      if (!landmarker || !collecting) {
        bitmap.close();
        return;
      }
      const idx = nextDueIndex(t);
      // In dense mode, run detection + scoreFn every frame so stateful
      // closures (blink/nod/shake counters) see all transitions. In normal
      // mode, skip frames that don't fall on a sample deadline.
      if (idx === -1 && !denseDetection) {
        bitmap.close();
        return;
      }
      try {
        const result = landmarker.detectForVideo(
          bitmap as unknown as HTMLCanvasElement,
          t,
        );
        const lm = result.faceLandmarks?.[0] ?? [];
        const bs = result.faceBlendshapes?.[0];
        const score = scoreFn(lm, bs);
        if (idx !== -1) {
          samples[idx] = score;
        }
        // The default face scorer carries a `visibility` field; the smile
        // scorer doesn't. Lighting warn fires only when visibility is
        // explicitly low, so the duck-typed access keeps it inert for smile.
        const vis = (score as unknown as { visibility?: number } | null)?.visibility;
        if (typeof vis === 'number' && vis < LIGHTING_REALTIME_WARN) {
          events.onLighting?.(true);
        }
        events.onSample?.(idx, t, score);
      } catch (err) {
        console.warn('[scorer] detect failed', err);
      } finally {
        bitmap.close();
      }
    },
    async stop() {
      collecting = false;
      // Aggregate is FrameScore-specific; smile mode supplies its own scoreFn
      // and aggregates separately on the caller side. Surface raw samples for
      // both modes; mean/visibility are best-effort based on FrameScore shape.
      const agg = looksLikeFrameScores(samples)
        ? aggregate(samples)
        : { mean: 0, visibility: 0, validFrames: 0 };
      const out = { mean: agg.mean, visibility: agg.visibility, samples: [...samples] };
      samples = [];
      deadlines = [];
      return out;
    },
    destroy() {
      destroyed = true;
      collecting = false;
      try {
        landmarker?.close();
      } catch {
        // ignore
      }
      landmarker = null;
    },
  };
}
